"use client";

import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchKYCStatusRequest, goToPreviousStep } from "@/store/slices/kyc.slice";
import StepIndicator from "@/components/kyc/StepIndicator";
import { GlobalLoadingScreen } from "@/components/common/GlobalLoadingScreen";
import BusinessDetailsStepNew from "@/components/kyc/BusinessDetailsStepNew";
import PersonalInformationStep from "@/components/kyc/PersonalInformationStep";
import DocumentUploadStep from "@/components/kyc/DocumentUploadStep";
import AddressVerificationStep from "@/components/kyc/AddressVerificationStep";
import VideoVerificationStep from "@/components/kyc/VideoVerificationStep";
import KycStatusStep from "@/components/kyc/KycStatusStep";

const BusinessInfo = () => {
  const dispatch = useAppDispatch();
  const { kycData, currentStep, completedSteps, rejectedSteps, isLoading } = useAppSelector((state) => state.kyc);
  const { userId, roles } = useAppSelector((state) => state.auth);
  const lastFetchedStoreIdRef = useRef<string | null>(null);

  const storeId = roles && roles.length > 0 && roles[0].storeId ? roles[0].storeId : userId || "";

  const handlePrevious = () => {
    dispatch(goToPreviousStep());
  };

  useEffect(() => {
    if (!storeId) return;
    if (lastFetchedStoreIdRef.current === storeId) return;
    lastFetchedStoreIdRef.current = storeId;
    dispatch(fetchKYCStatusRequest({ storeId }));
  }, [dispatch, storeId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <BusinessDetailsStepNew storeId={storeId} kycData={kycData} />;
      case 2:
        return <PersonalInformationStep storeId={storeId} kycData={kycData} onPrevious={handlePrevious} />;
      case 3:
        return <DocumentUploadStep storeId={storeId} kycData={kycData} onPrevious={handlePrevious} />;
      case 4:
        return <AddressVerificationStep storeId={storeId} kycData={kycData} onPrevious={handlePrevious} />;
      case 5:
        return <VideoVerificationStep storeId={storeId} kycData={kycData} onPrevious={handlePrevious} />;
      case 6:
      default:
        return <KycStatusStep storeId={storeId} />;
    }
  };

  if (isLoading && currentStep === 1) {
    return <GlobalLoadingScreen message="Loading KYC Status" />;
  }

  return (
    <div className="relative space-y-5">
      {isLoading && currentStep !== 1 && <GlobalLoadingScreen message="Processing" />}

      <div className="flex items-center justify-between mt-4">
        <h1 className="text-2xl font-semibold text-foreground">KYC</h1>
        <div className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded-full">
          Step {currentStep} of 6
        </div>
      </div>

      <div className="bg-card p-6 border rounded-[20px] shadow-sm">
        <StepIndicator
          currentStep={currentStep}
          completedSteps={completedSteps}
          rejectedSteps={rejectedSteps}
        />
      </div>

      <div>{renderCurrentStep()}</div>
    </div>
  );
};

export default BusinessInfo;